import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { doc, setDoc, onSnapshot, collection, query, where, getDocs } from 'firebase/firestore';
import { sendNotification } from '../utils/notifications';
import { db } from '../firebase';
import { useUser } from './UserContext';

const SocialContext = createContext();

export function SocialProvider({ children }) {
  const { user } = useUser();
  const [following, setFollowing] = useState([]);
  const [followers, setFollowers] = useState([]);

  // Charger les follows de l'utilisateur
  useEffect(() => {
    if (!user) {
      setFollowing([]);
      setFollowers([]);
      return;
    }

    // Qui je suis
    const unsubFollowing = onSnapshot(doc(db, 'social', user.id), (snapshot) => {
      if (snapshot.exists()) {
        setFollowing(snapshot.data().following || []);
      }
    });

    // Qui me suit
    const unsubFollowers = onSnapshot(
      query(collection(db, 'social'), where('following', 'array-contains', user.id)),
      (snapshot) => {
        const followerIds = snapshot.docs.map(doc => doc.id);
        setFollowers(followerIds);
      }
    );

    return () => {
      unsubFollowing();
      unsubFollowers();
    };
  }, [user]);

  const followUser = useCallback(async (targetUserId) => {
    if (!user || targetUserId === user.id) return;
    const updated = [...following, targetUserId];
    setFollowing(updated);
    await setDoc(doc(db, 'social', user.id), { following: updated }, { merge: true });

    // Notification
    sendNotification({
      recipientId: targetUserId,
      fromId: user.id,
      fromName: user.name,
      type: 'follow',
    });
  }, [user, following]);
  
  const unfollowUser = useCallback(async (targetUserId) => {
    if (!user) return;
    const updated = following.filter(id => id !== targetUserId);
    setFollowing(updated);
    await setDoc(doc(db, 'social', user.id), { following: updated }, { merge: true });
  }, [user, following]);

  const isFollowing = useCallback((targetUserId) => {
    return following.includes(targetUserId);
  }, [following]);

  const getFollowerCount = useCallback(async (targetUserId) => {
    const snapshot = await getDocs(
      query(collection(db, 'social'), where('following', 'array-contains', targetUserId))
    );
    return snapshot.size;
  }, []);

  return (
    <SocialContext.Provider value={{
      following, followers, followUser, unfollowUser, isFollowing, getFollowerCount,
    }}>
      {children}
    </SocialContext.Provider>
  );
}

export function useSocial() {
  return useContext(SocialContext);
}